// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import DashboardCards from "./dashboardCards";

import { Chart as Chrt } from "react-chartjs-2";
import { PDFDocument, rgb } from "pdf-lib";
import { saveAs } from "file-saver";
import { CategoryScale } from "chart.js";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, { decryptData } from "../../utils/Constants";
import SectionTitle from "../../components/molecules/SectionTitle";
import media from "../../media";
import { getDashboardDetailsThunk } from "../../store/thunks/dashboardThunk";
import moment from "moment";
import CustomLoader from "../../utils/CustomLoader";
import DashboardPendingRequests from "./dashboardPendingRequests";
import DashboardExpenseListing from "./dashboardExpenseListing";
import {
  getCurrencyListDetailThunk,
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getProjectCodeListThunk,
  getUserListWithDetailThunk,
} from "../../store/thunks/masterDataThunk";

Chart.register(CategoryScale, ChartDataLabels);

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function Dashboard() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const chartRef = useRef(null);

  // --------------------------
  // REDUX STATE
  // --------------------------
  const { role, lIUId, accessToken, userInfo } = useSelector(
    (state) => ({
      role: state.auth.role,
      lIUId: state.auth.lIUId,
      accessToken: state.auth.accessToken,
      userInfo: state.auth.userInfo,
    }),
    shallowEqual
  );

  const storeUserInfo = decryptData(userInfo);
  const storeLoggedInUserName = storeUserInfo?.user?.name;

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [dashboardDetails, setDashboardDetails] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("1Y");
  const [chartHeading, setChartHeading] = useState("");
  const [chartLabels, setChartLabels] = useState([]);
  const [chartValues, setChartValues] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [refreshDashboard, setRefreshDashboard] = useState(false);

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------
  const expenseReport = ["1Y", "6M", "1M"];
  const isChartReady = chartLabels.length > 0 && chartValues.length > 0;

  // --------------------------
  // USE EFFECTS
  // --------------------------

  // // Fetch dashboard details
  // useEffect(() => {
  //   setShowLoader(true);
  //   dispatch(getUserListWithDetailThunk())
  //     .unwrap()
  //     .then((res) => {
  //       setUsers(res || []);
  //     });
  //   dispatch(getDepartmentListThunk())
  //     .unwrap()
  //     .then((res) => {
  //       setDepartments(res || []);
  //     });
  //   dispatch(getProjectCodeListThunk())
  //     .unwrap()
  //     .then((res) => {
  //       setProjectCodes(res || []);
  //     });
  //   dispatch(getExpenseCodeListThunk())
  //     .unwrap()
  //     .then((res) => {
  //       setExpenseCodes(res || []);
  //     });
  //   dispatch(getCurrencyListDetailThunk())
  //     .unwrap()
  //     .then((res) => {
  //       setAllCurrencies(res || []);
  //     });
  //   const payload = {
  //     route: currentPath,
  //     role,
  //     lIUId,
  //     limitForPendingRequests: 5,
  //     limitForListOfExpenseRequests: 5,
  //   };
  //   dispatch(getDashboardDetailsThunk(payload)).then((response) => {
  //     if (response) setDashboardDetails(response.payload);
  //     setShowLoader(false);
  //   });
  // }, [dispatch, currentPath, role, lIUId, refreshDashboard]);

    const payload = {
      route: currentPath,
      role,
      lIUId,
      limitForPendingRequests: 5,
      limitForListOfExpenseRequests: 5,
    };
  useEffect(() => {
  setShowLoader(true);

  Promise.all([
    dispatch(getUserListWithDetailThunk()).unwrap(),
    dispatch(getDepartmentListThunk()).unwrap(),
    dispatch(getProjectCodeListThunk()).unwrap(),
    dispatch(getExpenseCodeListThunk()).unwrap(),
    dispatch(getCurrencyListDetailThunk()).unwrap(),
    dispatch(getDashboardDetailsThunk(payload)).unwrap(),
  ])
    .then(
      ([
        users,
        departments,
        projects,
        expenses,
        currencies,
        dashboard,
      ]) => {
        setUsers(users || []);
        setDepartments(departments || []);
        setProjectCodes(projects || []);
        setExpenseCodes(expenses || []);
        setAllCurrencies(currencies || []);
        setDashboardDetails(dashboard);
      }
    )
    .finally(() => setShowLoader(false));
}, [currentPath, role, lIUId, refreshDashboard]);


  // Update chart when data changes
  useEffect(() => {
    if (!dashboardDetails) return;
    const dataMapping = {
      "1Y": dashboardDetails?.twelveMonths || {},
      "6M": dashboardDetails?.sixMonths || {},
      "1M": dashboardDetails?.thirtyDays || {},
    };

    const items = dataMapping[selectedPeriod];
    setChartLabels(items?.labels || []);

    const values =
      selectedPeriod === "1Y"
        ? items?.twelveMonthValues
        : selectedPeriod === "6M"
        ? items?.sixMonthValues
        : items?.dayValues || [];
    setChartValues(values);

    const firstLabel = items?.labels?.[0] || selectedPeriod;
    const lastLabel =
      items?.labels?.[items.labels.length - 1] || selectedPeriod;
    setChartHeading(`${firstLabel} - ${lastLabel}`);
  }, [dashboardDetails, selectedPeriod]);

  // ---------------------------------------------
  // SECURITY HELPERS: ENCRYPTION / DECRYPTION UTILITIES
  // ---------------------------------------------

  // Gradient for chart
  const gradient = useMemo(() => {
    if (!chartRef.current) return null;
    const ctx = chartRef.current.ctx;
    const canvas = chartRef.current.canvas;
    const grad = ctx.createLinearGradient(0, canvas.height, 0, 0);
    grad.addColorStop(0, "#6d7eaf");
    grad.addColorStop(1, "#e0e0e2");
    return grad;
  }, [chartRef, chartValues]);

  const chartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: chartHeading,
          data: chartValues,
          backgroundColor: (context) => {
        const { chart } = context;
        const { ctx, chartArea } = chart;
        if (!chartArea) return null;

        const gradient = ctx.createLinearGradient(
          0,
          chartArea.bottom,
          0,
          chartArea.top
        );
        gradient.addColorStop(0, "#6d7eaf");
        gradient.addColorStop(1, "#e0e0e2");
        return gradient;
      },
          barThickness: 40,
          order: 2,
          borderRadius: 10,
          borderSkipped: false,
          datalabels: { color: "#7c7c7c" },
        },
        {
          type: "line",
          label: "ExpenseReport",
          data: chartValues,
          borderColor: "#b8c0d8",
          borderWidth: 1.5,
          fill: false,
          yAxisID: "y-right",
          datalabels: { display: false },
          order: 1,
          pointBackgroundColor: "#112c7d",
          pointBorderColor: "#112c7d",
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    }),
    [chartLabels, chartValues, gradient, chartHeading]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      animation: {
        duration: 0,
      },
      plugins: {
        legend: {
          position: "top",
          labels: {
            filter: (item) => item.text !== "ExpenseReport",
            boxWidth: 0,
          },
          onClick: () => {},
        },
        datalabels: {
          display: true,
          color: "black",
          anchor: "end",
          align: "top",
          font: { weight: "bold", size: 12 },
          formatter: (value) =>
            `${process.env.REACT_APP_DEFAULT_CURRENCY}${value}`,
        },
        tooltip: {
          backgroundColor: "#fff",
          titleColor: "#000",
          bodyColor: "#444",
          borderColor: "#F4F4F5",
          borderWidth: 1,
          displayColors: false,
          callbacks: {
            title: (ctx) => `Month: ${ctx[0].label}`,
            label: (ctx) =>
              `${
                process.env.REACT_APP_DEFAULT_CURRENCY
              }${ctx.raw.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}`,
          },
        },
      },
      scales: {
        "y-right": { display: false },
        x: {
          grid: {
            display: true,
            drawTicks: false,
            drawBorder: false,
            color: "rgba(0,0,0,0.1)",
            borderDash: [2, 4],
            z: 0,
          },
        },
      },
    }),
    [chartValues]
  );

  // Total expenses
  const calculateTotal = useMemo(() => {
    if (!dashboardDetails) return "0.00";
    const values =
      selectedPeriod === "1Y"
        ? dashboardDetails?.twelveMonths?.twelveMonthValues || []
        : selectedPeriod === "6M"
        ? dashboardDetails?.sixMonths?.sixMonthValues || []
        : dashboardDetails?.thirtyDays?.dayValues || [];
    const total = values.reduce(
      (acc, val) =>
        acc + (typeof val === "object" ? Number(val || 0) : Number(val || 0)),
      0
    );
    const integerPart = Math.floor(total).toLocaleString();
    const decimalPart = (total % 1).toFixed(2).split(".")[1];
    return `
  <div class="font-weight-bold font-size-sm">
    <span class="font-size-h2 mr-2 customPrice">
      ${process.env.REACT_APP_DEFAULT_CURRENCY}${integerPart}${
      decimalPart !== "00" ? `<small>.${decimalPart}</small>` : ""
    }
    </span>
  </div>
`;
  }, [dashboardDetails, selectedPeriod]);

  // Dashboard cards
  const dashboardCards = useMemo(
    () => [
      {
        isVisible: true,
        item: "Today's Expenses",
        name: "todayExpenses",
        itemValue: dashboardDetails,
        itemIcon: media.dashboardExpense.svg,
        isCurrency: "yes",
      },
      {
        isVisible: true,
        item: "Total Expenses",
        name: "totalExpenses",
        itemValue: dashboardDetails,
        itemIcon: media.dashboardStat.svg,
        isCurrency: "yes",
      },
      {
        isVisible: role !== process.env.REACT_APP_EROLE,
        item: "Last Month",
        name: "lastMonth",
        itemValue: dashboardDetails,
        itemIcon: media.dahboardCalender.svg,
        isCurrency: "yes",
      },
      {
        isVisible: role !== process.env.REACT_APP_EROLE,
        item: "Total Pending Requests",
        name: "totalPendingRequests",
        itemValue: dashboardDetails,
        itemIcon: media.pendingApprovals.svg,
        isCurrency: "no",
      },
    ],
    [dashboardDetails, role]
  );

  const downloadPDF = async () => {
    const chartCanvas = chartRef.current.canvas;
    const chartImage = chartCanvas.toDataURL("image/png");
    // Extract chart data
    const labels = chartRef.current.data.labels;
    const dataset = chartRef.current.data.datasets[0];

    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();
    const pageWidth = 595; // Width for A4 size in points
    const pageHeight = 842; // Height for A4 size in points

    // ** NEW ** Embed company logo image (replace with your logo URL or data)
    const companyLogoUrl = "/PdfLogo.png"; // Replace this URL with the actual logo path
    const logoImageBytes = await fetch(companyLogoUrl).then((res) =>
      res.arrayBuffer()
    );
    const logoImage = await pdfDoc.embedPng(logoImageBytes); // or embedJpg if JPG format

    const drawChartOnPage = async (page) => {
      // Embed the chart image into the PDF
      const pngImage = await pdfDoc.embedPng(chartImage);
      page.drawImage(pngImage, {
        x: 20,
        y: pageHeight - 370,
        width: 550,
        height: 300,
      });
    };

    const drawTextOnPage = (page, text, yPosition) => {
      page.drawText(text, {
        x: 20,
        y: yPosition,
        size: 12,
        color: rgb(0, 0, 0),
      });
    };

    const addExpenseDetails = (page, startY) => {
      page.drawText("Expense Details:", {
        x: 20,
        y: startY,
        size: 16,
        color: rgb(0, 0, 0),
      });
      return startY - 20;
    };

    // Start creating pages
    let page = pdfDoc.addPage([pageWidth, pageHeight]);
    let yPosition = pageHeight - 100;

    // Add chart title and company logo side by side
    page.drawText("Expense Report", {
      x: 20,
      y: pageHeight - 50,
      size: 20,
      color: rgb(0, 0, 0),
    });

    page.drawImage(logoImage, {
      x: pageWidth - 120, // Adjust X position based on the logo width
      y: pageHeight - 60, // Adjust Y position based on the logo height
      width: 100, // Set the width of the logo
      height: 30, // Set the height of the logo
    });

    // Add chart
    await drawChartOnPage(page);

    // Initialize for expense details
    yPosition -= 300; // Adjust yPosition based on chart height
    yPosition = addExpenseDetails(page, yPosition); // Add "Expense Details" title only on the first page

    let currentPageIndex = 0;
    const linesPerPage = Math.floor((pageHeight - 100 - 300 - 20) / 20); // Calculate how many lines fit per page
    const totalLines = labels.length;

    const addTextToPage = () => {
      let startIndex = currentPageIndex * linesPerPage;
      let endIndex = Math.min(startIndex + linesPerPage, totalLines);

      if (endIndex <= totalLines) {
        for (let i = startIndex; i < endIndex; i++) {
          const label = labels[i];
          const value = dataset.data[i];
          const currencySymbol = "Rs. ";
          drawTextOnPage(
            page,
            `${label}: ${currencySymbol}${value} (${moment
              .utc(new Date())
              .local()
              .format("YYYY")})`,
            yPosition
          );
          yPosition -= 20;
        }

        if (endIndex < totalLines) {
          // If more lines to add, create a new page
          currentPageIndex++;
          page = pdfDoc.addPage([pageWidth, pageHeight]);
          yPosition = pageHeight - 50; // Reset position on new page without re-adding the "Expense Details" title
          addTextToPage(); // Recurse to add remaining lines without re-adding "Expense Details" title
        }
      }
    };

    addTextToPage(); // Start adding text

    // Save the PDF and trigger download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    saveAs(blob, "expenseReport.pdf");
  };

  const shouldShowLoader =
  showLoader ||
  !dashboardDetails ||
  Object.keys(dashboardDetails).length === 0 ||
  !isChartReady;

  if (
    shouldShowLoader
  ) {
    return <CustomLoader showLoader={true} />;
  }

 
  const refreshDashboardList = () => {
    console.log("Toggling refreshDashboard");
    setRefreshDashboard((prev) => !prev);
  };

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <TopContainerHeader
        sectionNameClass="col-lg-7 text-left commonTitleText"
        sectionTitle={`Hi, ${storeLoggedInUserName}`}
        sectionDescription="<p>Welcome to Expense Management System</p>"
        hasSectionButton={true}
        hasSearch={false}
        name="Add your expenses"
        addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
        btnType="dshButton"
        addFormModalOpen={isAddModalOpen}
        addFormSetIsModalOpen={setIsAddModalOpen}
        addFormModalShowLoader={setShowLoader}
        onButtonClick={() => setIsAddModalOpen(true)}
      />

      {/* Dashboard Cards */}
      <div className="row">
        {dashboardCards.map(
          (card, i) =>
            card.isVisible && (
              <div key={i} className="col-lg-3">
                <DashboardCards {...card} />
              </div>
            )
        )}
      </div>

      <div className="row">
        {/* Expense Report Chart */}
        <div
          className={
            role === process.env.REACT_APP_EROLE
              ? "col-sm-12 cardParent"
              : "col-sm-9 cardParent"
          }
        >
          <div className="card bgi-no-repeat card-stretch gutter-b border-2 rounded-lg p-5">
            <div className="d-flex justify-content-between mb-6 align-items-center grapgWrap">
              <div className="graph-col">
                <SectionTitle
                  sectionNameClass="graphHeading"
                  sectionTitle="Expense Report"
                  sectionDescription={calculateTotal}
                />
              </div>
              <div className="graph-col">
                <div className="card-toolbar m-0">
                  <ul className="nav nav-pills nav-pills-sm monthGraph">
                    {expenseReport.map((period, idx) => (
                      <li
                        className="nav-item"
                        key={idx}
                        onClick={() => setSelectedPeriod(period)}
                      >
                        <a
                          className={`nav-link text-grey-custom cursor-pointer border border-2 border-transparent text-size-12 ${
                            period === selectedPeriod ? "activeMonth" : ""
                          }`}
                        >
                          {period}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card-toolbar m-0">
                  <button
                    title="Download PDF"
                    className="btn dashboard-pdf-button"
                    onClick={downloadPDF}
                  >
                    <i className="fas fa-file-download"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="chart-area">
              {/* <pre>chartLabels:{JSON.stringify(chartLabels, null, 2)}</pre>
              <pre>chartValues:{JSON.stringify(chartValues, null, 2)}</pre>
              <pre>chartData:{JSON.stringify(chartData, null, 2)}</pre> */}
             <Chrt
                ref={chartRef}
                type="bar"
                data={chartData}
                options={chartOptions}
              />
            </div>
          </div>
        </div>

        {/* Pending Requests */}
        {role !== process.env.REACT_APP_EROLE && (
          <div className="col-sm-3 cardParent">
            <div className="card card-stretch gutter-b b-radius-16">
              <DashboardPendingRequests
                dashboardPendingRequestsData={
                  dashboardDetails?.dashboardPendingRequestsData
                }
                usersList={users}
                departmentsList={departments}
              />
            </div>
          </div>
        )}
      </div>

      {/* Expense Listing */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card bgi-no-repeat card-stretch border-2 rounded-lg p-5">
            <DashboardExpenseListing
              dashboardExpenseListingData={
                dashboardDetails?.dashboardExpenseListingData
              }
              usersList={users}
              departmentsList={departments}
              projectCodeList={projectCodes}
              expenseCodeList={expenseCodes}
              currencyCodeList={allCurrencies}
              onRefresh={refreshDashboardList}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
